import React, { useCallback, useEffect, useMemo, useState } from 'react'
import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import DataGrid from '@/components/data/DataGrid'
import Button from '@/components/ui/Button'
import { designTokens } from '@/utils/design-tokens'
import { useShowTasks } from '@/hooks/useShowTasks'
import { formatDateForAPI } from '@/components/ui/DateRangePicker/utils'
import {
  TaskSummaryService,
  type TaskSummaryTotalBusinessItem,
  type TaskSummaryTotalDepartmentItem,
} from '@/api'
import BusinessDetailPanel from '@/pages/dashboard/DetailDashboard/components/BusinessDetailPanel'
import DepartmentDetailPanel from '@/pages/dashboard/DetailDashboard/components/DepartmentDetailPanel'

interface BusinessDepartmentSummaryProps {
  workspaceId?: string
  /** 조회 범위 (스냅샷 시점은 X-As-Of 헤더로 별도 전달) */
  startDate?: Date | null
  endDate?: Date | null
  /** 사업 필터 (미선택이면 전체) */
  businessId?: string
  /** 섹션 타이틀 접두어 (사업 미선택이면 '전체', 선택 시 사업명) */
  titlePrefix?: string
}

/** 진행율/금액 등 사업·부서 공통 지표 컬럼 (사업명/부서명 + 액션 컬럼 뒤에 붙는다) */
const metricColumnDefs: ColDef[] = [
  {
    headerName: '전체 수행업무',
    field: 'task_item.total.total_count',
    width: 140, minWidth: 140, maxWidth: 140,
    sortable: true, filter: false, suppressSizeToFit: true,
    valueFormatter: (p) => p.value == null ? '-' : `${p.value.toLocaleString()}개`,
    cellStyle: { textAlign: 'right', padding: '0 16px' },
  },
  {
    headerName: '완료 수행업무',
    field: 'task_item.total.completed_count',
    width: 140, minWidth: 140, maxWidth: 140,
    sortable: true, filter: false, suppressSizeToFit: true,
    valueFormatter: (p) => p.value == null ? '-' : `${p.value.toLocaleString()}개`,
    cellStyle: { textAlign: 'right', padding: '0 16px' },
  },
  {
    headerName: '수행업무 진행율',
    field: 'task_item.total.count_percentage',
    width: 140, minWidth: 140, maxWidth: 140,
    sortable: true, filter: false, suppressSizeToFit: true,
    valueFormatter: (p) => p.value == null ? '-' : `${p.value}%`,
    cellStyle: { textAlign: 'right', padding: '0 16px' },
  },
  {
    headerName: '전체 세부실행과제',
    field: 'task.total.total_count',
    width: 140, minWidth: 140, maxWidth: 140,
    sortable: true, filter: false, suppressSizeToFit: true,
    valueFormatter: (p) => p.value == null ? '-' : `${p.value.toLocaleString()}개`,
    cellStyle: { textAlign: 'right', padding: '0 16px' },
  },
  {
    headerName: '완료 세부실행과제',
    field: 'task.total.completed_count',
    width: 140, minWidth: 140, maxWidth: 140,
    sortable: true, filter: false, suppressSizeToFit: true,
    valueFormatter: (p) => p.value == null ? '-' : `${p.value.toLocaleString()}개`,
    cellStyle: { textAlign: 'right', padding: '0 16px' },
  },
  {
    headerName: '세부실행과제 진행율',
    field: 'task.total.count_percentage',
    width: 140, minWidth: 140, maxWidth: 140,
    sortable: true, filter: false, suppressSizeToFit: true,
    valueFormatter: (p) => p.value == null ? '-' : `${p.value}%`,
    cellStyle: { textAlign: 'right', padding: '0 16px' },
  },
  {
    headerName: '전체 예산액',
    field: 'task_budget.total.total_target_figure',
    width: 140, minWidth: 140, maxWidth: 140,
    sortable: true, filter: false, suppressSizeToFit: true,
    valueFormatter: (p) => p.value == null ? '-' : `${p.value.toLocaleString()}원`,
    cellStyle: { textAlign: 'right', padding: '0 16px' },
  },
  {
    headerName: '집행 예산액',
    field: 'task_budget.total.total_done_figure',
    width: 140, minWidth: 140, maxWidth: 140,
    sortable: true, filter: false, suppressSizeToFit: true,
    valueFormatter: (p) => p.value == null ? '-' : `${p.value.toLocaleString()}원`,
    cellStyle: { textAlign: 'right', padding: '0 16px' },
  },
  {
    headerName: '예산 집행율',
    field: 'task_budget.total.figure_percentage',
    width: 140, minWidth: 140, maxWidth: 140,
    sortable: true, filter: false, suppressSizeToFit: true,
    valueFormatter: (p) => p.value == null ? '-' : `${p.value}%`,
    cellStyle: { textAlign: 'right', padding: '0 16px' },
  },
  {
    headerName: '성과 과제(전체)',
    field: 'task_performance.total.total_count',
    width: 140, minWidth: 140, maxWidth: 140,
    sortable: true, filter: false, suppressSizeToFit: true,
    valueFormatter: (p) => p.value == null ? '-' : `${p.value.toLocaleString()}개`,
    cellStyle: { textAlign: 'right', padding: '0 16px' },
  },
  {
    headerName: '성과 과제(달성)',
    field: 'task_performance.total.completed_count',
    width: 140, minWidth: 140, maxWidth: 140,
    sortable: true, filter: false, suppressSizeToFit: true,
    valueFormatter: (p) => p.value == null ? '-' : `${p.value.toLocaleString()}개`,
    cellStyle: { textAlign: 'right', padding: '0 16px' },
  },
  {
    headerName: '성과 달성율',
    field: 'task_performance.total.count_percentage',
    width: 140, minWidth: 140, maxWidth: 140,
    sortable: true, filter: false, suppressSizeToFit: true,
    valueFormatter: (p) => p.value == null ? '-' : `${p.value}%`,
    cellStyle: { textAlign: 'right', padding: '0 16px' },
  },
]

const GRID_HEIGHT = 40 + 10 * 42 + 60 + 2 // header + (10 rows * rowHeight) + pagination + border

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2
    style={{
      fontFamily: designTokens.typography.fontFamily.sans[0],
      fontSize: '18px',
      fontWeight: 700,
      color: designTokens.colors.gray[6],
      marginBottom: '12px',
    }}
  >
    {children}
  </h2>
)

const ErrorBox: React.FC<{ message: string }> = ({ message }) => (
  <div
    className="flex items-center justify-center h-32 text-center"
    style={{
      fontFamily: designTokens.typography.fontFamily.sans[0],
      fontSize: designTokens.typography.fontSize['body-medium'][0],
      color: designTokens.colors.gray[4],
    }}
  >
    {message}
  </div>
)

const BusinessDepartmentSummary: React.FC<BusinessDepartmentSummaryProps> = ({
  workspaceId,
  startDate,
  endDate,
  businessId,
  titlePrefix,
}) => {
  const [businessSummaryData, setBusinessSummaryData] = useState<TaskSummaryTotalBusinessItem[]>([])
  const [isLoadingBusinessSummary, setIsLoadingBusinessSummary] = useState(false)
  const [businessSummaryError, setBusinessSummaryError] = useState<string | null>(null)

  const [departmentSummaryData, setDepartmentSummaryData] = useState<TaskSummaryTotalDepartmentItem[]>([])
  const [isLoadingDepartmentSummary, setIsLoadingDepartmentSummary] = useState(false)
  const [departmentSummaryError, setDepartmentSummaryError] = useState<string | null>(null)

  // 상세 패널 상태
  const [selectedBusiness, setSelectedBusiness] = useState<TaskSummaryTotalBusinessItem | null>(null)
  const [isBusinessPanelOpen, setIsBusinessPanelOpen] = useState(false)
  const [selectedDepartment, setSelectedDepartment] = useState<TaskSummaryTotalDepartmentItem | null>(null)
  const [isDepartmentPanelOpen, setIsDepartmentPanelOpen] = useState(false)

  // 사업별 현황 로드
  const fetchBusinessSummary = useCallback(async () => {
    if (!workspaceId) return
    setIsLoadingBusinessSummary(true)
    setBusinessSummaryError(null)
    try {
      // 스냅샷 시점은 X-As-Of 헤더, 조회 범위는 start/end로 전달
      const response = await TaskSummaryService.appsTaskViewsGetTaskSummaryTotalByBusiness({
        workspaceId,
        businessId: businessId || undefined,
        startDate: startDate ? formatDateForAPI(startDate) : undefined,
        endDate: endDate ? formatDateForAPI(endDate) : undefined,
      })
      setBusinessSummaryData(response ?? [])
    } catch (err) {
      console.error('Failed to fetch business summary data:', err)
      setBusinessSummaryError('사업별 현황 데이터를 불러오는데 실패했습니다.')
      setBusinessSummaryData([])
    } finally {
      setIsLoadingBusinessSummary(false)
    }
  }, [workspaceId, businessId, startDate, endDate])

  // 부서별 현황 로드
  const fetchDepartmentSummary = useCallback(async () => {
    if (!workspaceId) return
    setIsLoadingDepartmentSummary(true)
    setDepartmentSummaryError(null)
    try {
      // 스냅샷 시점은 X-As-Of 헤더, 조회 범위는 start/end로 전달
      const response = await TaskSummaryService.appsTaskViewsGetTaskSummaryTotalByDepartment({
        workspaceId,
        businessId: businessId || undefined,
        startDate: startDate ? formatDateForAPI(startDate) : undefined,
        endDate: endDate ? formatDateForAPI(endDate) : undefined,
      })
      setDepartmentSummaryData(response ?? [])
    } catch (err) {
      console.error('Failed to fetch department summary data:', err)
      setDepartmentSummaryError('부서별 현황 데이터를 불러오는데 실패했습니다.')
      setDepartmentSummaryData([])
    } finally {
      setIsLoadingDepartmentSummary(false)
    }
  }, [workspaceId, businessId, startDate, endDate])

  useEffect(() => { fetchBusinessSummary() }, [fetchBusinessSummary])
  useEffect(() => { fetchDepartmentSummary() }, [fetchDepartmentSummary])

  // "상세보기" 버튼 → 해당 상세 패널 열기 (사업/부서 데이터 형태로 구분)
  const ActionButtonRenderer = useCallback((params: ICellRendererParams) => {
    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation()
      e.preventDefault()
      if (!params.data) return
      if ('business_name' in params.data) {
        setSelectedBusiness(params.data)
        setIsBusinessPanelOpen(true)
      } else if ('department_name' in params.data) {
        setSelectedDepartment(params.data)
        setIsDepartmentPanelOpen(true)
      }
    }
    return (
      <div
        onClick={(e) => { e.stopPropagation() }}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}
      >
        <Button
          size="xsmall"
          color="light"
          onClick={handleClick}
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '12px',
            fontWeight: 600,
            padding: '4px 12px',
          }}
        >
          상세보기
        </Button>
      </div>
    )
  }, [])

  const actionColumn: ColDef = useMemo(() => ({
    headerName: '액션',
    field: 'action',
    width: 100, minWidth: 100, maxWidth: 100,
    sortable: false, filter: false, suppressSizeToFit: true,
    cellRenderer: ActionButtonRenderer,
    cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0' },
  }), [ActionButtonRenderer])

  // 워크스페이스 설정에서 세부실행과제 표시가 OFF 면 세부실행과제 열(task.*)을 뺀다
  const showTasks = useShowTasks()
  const visibleMetricColumnDefs = useMemo(
    () => (showTasks ? metricColumnDefs : metricColumnDefs.filter(col => !col.field?.startsWith('task.'))),
    [showTasks],
  )

  const businessColumnDefs: ColDef[] = useMemo(() => [
    { headerName: '사업명', field: 'business_name', flex: 2, minWidth: 300, sortable: true, filter: true, suppressSizeToFit: false },
    actionColumn,
    ...visibleMetricColumnDefs,
  ], [actionColumn, visibleMetricColumnDefs])

  const departmentColumnDefs: ColDef[] = useMemo(() => [
    { headerName: '부서명', field: 'department_name', flex: 2, minWidth: 300, sortable: true, filter: true, suppressSizeToFit: false },
    actionColumn,
    ...visibleMetricColumnDefs,
  ], [actionColumn, visibleMetricColumnDefs])

  return (
    <>
      {/* 사업별 현황 */}
      <div className="mt-6">
        <SectionTitle>{titlePrefix ? `${titlePrefix} ` : ''}누적 사업별 현황</SectionTitle>
        <div className="bg-white rounded-lg shadow-sm border" style={{ borderColor: designTokens.colors.gray[1] }}>
          <div className="w-full">
            {businessSummaryError ? (
              <ErrorBox message={businessSummaryError} />
            ) : (
              <DataGrid
                columnDefs={businessColumnDefs}
                rowData={businessSummaryData}
                loading={isLoadingBusinessSummary}
                headerHeight={40}
                rowHeight={42}
                height={GRID_HEIGHT}
                animateRows={true}
                pagination={true}
                paginationPageSize={10}
                paginationPageSizeSelector={[10, 20, 50]}
                paginationAutoPageSize={false}
                onGridReady={(params) => params.api.sizeColumnsToFit()}
                enableRangeSelection={true}
                rangeSelectionExcludeColumns={['action']}
                enableCellCopy={true}
                suppressRowClickSelection={true}
              />
            )}
          </div>
        </div>
      </div>

      {/* 부서별 현황 */}
      <div className="mt-6">
        <SectionTitle>{titlePrefix ? `${titlePrefix} ` : ''}누적 부서별 현황</SectionTitle>
        <div className="bg-white rounded-lg shadow-sm border" style={{ borderColor: designTokens.colors.gray[1] }}>
          <div className="w-full">
            {departmentSummaryError ? (
              <ErrorBox message={departmentSummaryError} />
            ) : (
              <DataGrid
                columnDefs={departmentColumnDefs}
                rowData={departmentSummaryData}
                loading={isLoadingDepartmentSummary}
                headerHeight={40}
                rowHeight={42}
                height={GRID_HEIGHT}
                animateRows={true}
                pagination={true}
                paginationPageSize={10}
                paginationPageSizeSelector={[10, 20, 50]}
                paginationAutoPageSize={false}
                onGridReady={(params) => params.api.sizeColumnsToFit()}
                enableRangeSelection={true}
                rangeSelectionExcludeColumns={['action']}
                enableCellCopy={true}
                suppressRowClickSelection={true}
              />
            )}
          </div>
        </div>
      </div>

      {/* 상세 패널 (상세 현황 컴포넌트 재사용) */}
      <BusinessDetailPanel
        isOpen={isBusinessPanelOpen}
        onClose={() => { setIsBusinessPanelOpen(false); setSelectedBusiness(null) }}
        selectedBusiness={selectedBusiness}
        startDate={startDate}
        endDate={endDate}
        periodViewType="period"
      />
      <DepartmentDetailPanel
        isOpen={isDepartmentPanelOpen}
        onClose={() => { setIsDepartmentPanelOpen(false); setSelectedDepartment(null) }}
        selectedDepartment={selectedDepartment}
        startDate={startDate}
        endDate={endDate}
        periodViewType="period"
      />
    </>
  )
}

export default BusinessDepartmentSummary
